import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import Checkbox from "@cayuse/shared-react.components.form.inputs.checkbox";
import CustomDropdown from "@cayuse/shared-react.custom-dropdown";
import Icon from "@cayuse/shared-react.components.icon";
import { isFunction } from "@cayuse/shared-react.utils.type-checks";
import IconButton from "@cayuse/shared-react.components.icon-button";
import Button from "@cayuse/shared-react.components.button";

import { useDictionary, useGroupRoles } from "hooks";
import { compileConditions, findOptionTypes } from "./utils";
import { ROLE_SP_PROPOSAL_REVIEWER, RULE_TYPES } from "globals/constants";

import TeamTypeDisplay from "containers/RulesOld/List/ActionBuilder/TeamTypeDisplay";
import SimpleTypeDisplay from "containers/RulesOld/List/ActionBuilder/SimpleTypeDisplay";
import { useRuleBuilderContext } from "containers/Rules/context/useRuleBuilderContext";

import styles from "./styles.css";

const componentHash = {
    [RULE_TYPES.AD_HOC_NOTIFICATION]: TeamTypeDisplay, // if this diverges from Team Reviews, make a new Builder
    [RULE_TYPES.AD_HOC_REVIEW]: TeamTypeDisplay,
    [RULE_TYPES.RT_CERTIFICATION]: SimpleTypeDisplay,
    [RULE_TYPES.RT_REVIEW]: SimpleTypeDisplay,
    [RULE_TYPES.DISCLOSURE]: SimpleTypeDisplay,
    [RULE_TYPES.UNIT_REVIEW]: SimpleTypeDisplay,
    [RULE_TYPES.INFOREADY_REVIEW]: TeamTypeDisplay,
};

const ActionBuilder = ({ rule, isNew, handleRuleSave, handleRuleCancel }) => {
    const [rollupEnabled, setRollupEnabled] = useState(rule.rollupEnabled);

    const { showUnitReviewAction, showRTActions } = useMemo(() => {
        if (rule.condition && rule.condition.operands) {
            return compileConditions(rule.condition.operands);
        }
        return {};
    }, [rule.condition?.operands, rule.isConditionEditing]);

    const {
        ruleTypeHash,
        onRuleChange,
        setEditingAction,
        setFormChanged,
        editingCondition,
        isEditingAction,
    } = useRuleBuilderContext();
    
    const { getGroupRoleByName } = useGroupRoles();
    const dictionary = useDictionary("ruleBuilder");
    const ruleType = rule.ruleType;
    const hasCondition = !!rule.condition;

    const Condition =
        ruleType in componentHash ? componentHash[ruleType] : undefined;

    const ruleOptions = () => {
        const optionTypes = [];
        findOptionTypes(rule, optionTypes);

        return Object.values(ruleTypeHash)
            .filter((ruleHash) => {
                if (!showRTActions) {
                    return (
                        ruleHash.type !== "RT_REVIEW" &&
                        ruleHash.type !== "RT_CERTIFICATION" &&
                        ruleHash.type !== "DISCLOSURE"
                    );
                }
                return true;
            })
            .filter(
                (ruleHash) =>
                    ruleHash.type !== "UNIT_REVIEW" || showUnitReviewAction
            )
            .map((ruleHash) => ({
                id: ruleHash.type,
                text: ruleHash.label,
                key: ruleHash.type,
                value: ruleHash.type ? ruleHash.type : "",
            }))
            .sort((a, b) => a.text.localeCompare(b.text));
    };

    const handleChange = (event, { value }) => {
        if (
            [
                RULE_TYPES.UNIT_REVIEW,
                RULE_TYPES.DISCLOSURE,
                RULE_TYPES.RT_REVIEW,
            ].includes(value)
        ) {
            const spProposalReviewerRole = getGroupRoleByName(
                ROLE_SP_PROPOSAL_REVIEWER
            );
            rule.targetId = spProposalReviewerRole.id;
        }

        if (isFunction(onRuleChange)) {
            onRuleChange({ ...rule, ruleType: value });
        }

        setFormChanged(true);
    };

    const handleEdit = () => {
        rule.isActionEditing = true;
        setEditingAction(true);
    };

    const handleRollupChange = (event, data) => {
        setRollupEnabled(data.checked);
        rule.rollupEnabled = data.checked;
    };

    const disableSaveButton = useMemo(
        () =>
            !ruleType ||
            ([
                RULE_TYPES.AD_HOC_NOTIFICATION,
                RULE_TYPES.AD_HOC_REVIEW,
                RULE_TYPES.INFOREADY_REVIEW,
            ].includes(ruleType) &&
                !rule.targetId),
        [ruleType, rule.targetId]
    );

    const enableAdditionalReview = useMemo(
        () =>
            ruleType === RULE_TYPES.UNIT_REVIEW ||
            ruleType === RULE_TYPES.RT_REVIEW,
        [ruleType]
    );

    const handleRuleChanged = (rule) => {
        setFormChanged(true);
        onRuleChange(rule);
    };

    return (
        <div data-component="Rules\List\ActionBuilder">
            {hasCondition && !rule.isConditionEditing && (
                <div className={styles.actionContainer}>
                    <div>
                        {rule.isActionEditing && (
                            <div className={styles.actionDropdownWrapper} role="application">
                                <label>{dictionary.action ?? "Action"}</label>
                                <CustomDropdown
                                    customClassname={styles.actionDropdown}
                                    label={`Select ${dictionary.action ?? "Action"}`}
                                    options={ruleOptions()}
                                    value={ruleType ?? ""}
                                    onChange={handleChange}
                                    isCustomized={true}
                                    isCustomStyling={true}
                                    isCustomOnChange={true}
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.actionTypeWrapper}>
                        <div className={styles.actionTypeContent}>
                            {Condition && (
                                <Condition
                                    dictionary={dictionary}
                                    isNew={
                                        rule.isAddingMode ||
                                        rule.isActionEditing
                                    }
                                    rule={rule}
                                    onRuleChange={handleRuleChanged}
                                />
                            )}
                            {enableAdditionalReview &&
                                (!rule.rollupEnabled &&
                                !rule.isActionEditing ? null : rule.rollupEnabled &&
                                  !rule.isActionEditing ? (
                                    <div className={styles.iconWithText}>
                                        <Icon name="check" />
                                        <span>
                                            {dictionary.conditionBuilder
                                                .additionalReview ||
                                                "Require Additional Review"}
                                        </span>
                                    </div>
                                ) : (
                                    <Checkbox
                                        label={
                                            dictionary.conditionBuilder
                                                .additionalReview ||
                                            "Require Additional Review"
                                        }
                                        checked={rollupEnabled}
                                        disabled={!rule.isActionEditing}
                                        onChange={handleRollupChange}
                                    />
                                ))}
                        </div>
                        {!isNew && !rule.isActionEditing && (
                            <IconButton
                                secondary
                                labelPosition="left"
                                icon="pencil"
                                onClick={handleEdit}
                                disabled={editingCondition || isEditingAction}
                                className={styles.editConditionButton}
                            >
                                {dictionary.conditionBuilder.editAction ||
                                    "Edit Action"}
                            </IconButton>
                        )}
                    </div>
                    {rule.isActionEditing && (
                        <div className={styles.saveButtonGroup}>
                            <Button
                                type="button"
                                secondary
                                onClick={handleRuleCancel}
                            >
                                {dictionary.cancelNewRule || "Cancel"}
                            </Button>
                            <Button
                                type="button"
                                disabled={disableSaveButton}
                                onClick={handleRuleSave}
                            >
                                {dictionary.saveNewRule || "Save"}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

ActionBuilder.propTypes = {
    isNew: PropTypes.bool,
    rule: PropTypes.shape({
        type: PropTypes.string,
    }).isRequired,
};

export default ActionBuilder;
